import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { BtnGhost } from './MarketplaceAtoms.js';
import { AdminAlertDot, AdminAlertCount } from '../../lib/adminMenuBadges.js';
import {
  fetchAdminSeguimientoReadIds,
  markAdminSeguimientoRead,
  markAdminSeguimientoUnread,
  markAllAdminSeguimientoRead,
} from '../../lib/adminSeguimientoLecturas.js';

/**
 * Hook: lecturas por sección admin (checkbox + marcar todas).
 * @param {{ adminId?: string, section: string, rows: object[], getItemId?: (row: object) => string, onBadgesRefresh?: () => void, onError?: (msg: string) => void }} opts
 */
export function useAdminSeguimiento({ adminId, section, rows, getItemId = r => r?.id, onBadgesRefresh, onError }) {
  const [readIds, setReadIds] = useState(() => new Set());
  const [readBusyId, setReadBusyId] = useState(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const itemIds = useMemo(
    () => [...new Set((rows ?? []).map(getItemId).filter(Boolean))],
    [rows, getItemId]
  );

  const syncReadIds = useCallback(async () => {
    if (!adminId || !section || !itemIds.length) {
      setReadIds(new Set());
      return;
    }
    const read = await fetchAdminSeguimientoReadIds(adminId, section, itemIds);
    setReadIds(read);
  }, [adminId, section, itemIds]);

  useEffect(() => {
    void syncReadIds();
  }, [syncReadIds]);

  const setReadState = useCallback(
    async (itemId, leida) => {
      if (!adminId || !section || !itemId) return;
      setReadBusyId(itemId);
      try {
        if (leida) {
          const { error } = await markAdminSeguimientoRead(adminId, section, itemId);
          if (error) throw error;
          setReadIds(prev => new Set([...prev, itemId]));
        } else {
          const { error } = await markAdminSeguimientoUnread(adminId, section, itemId);
          if (error) throw error;
          setReadIds(prev => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
          });
        }
        onBadgesRefresh?.();
      } catch (e) {
        onError?.(e?.message || 'No se pudo actualizar el estado de lectura');
      } finally {
        setReadBusyId(null);
      }
    },
    [adminId, section, onBadgesRefresh, onError]
  );

  const marcarTodasLeidas = useCallback(async () => {
    if (!adminId || !section || markingAllRead || !itemIds.length) return;
    setMarkingAllRead(true);
    onError?.('');
    try {
      const { error } = await markAllAdminSeguimientoRead(adminId, section, itemIds);
      if (error) throw error;
      setReadIds(new Set(itemIds));
      onBadgesRefresh?.();
    } catch (e) {
      onError?.(e?.message || 'No se pudieron marcar todas como leídas');
    } finally {
      setMarkingAllRead(false);
    }
  }, [adminId, section, itemIds, markingAllRead, onBadgesRefresh, onError]);

  const isUnread = useCallback(itemId => !!itemId && !readIds.has(itemId), [readIds]);
  const unreadCount = useMemo(() => itemIds.filter(id => !readIds.has(id)).length, [itemIds, readIds]);

  return {
    readIds,
    readBusyId,
    markingAllRead,
    setReadState,
    marcarTodasLeidas,
    isUnread,
    unreadCount,
    syncReadIds,
  };
}

/** Botón «Marcar todas como leídas» entre pestañas. */
export function AdminSeguimientoMarcarTodas({
  unreadCount,
  markingAllRead,
  onMarcarTodas,
  style,
}) {
  if (unreadCount < 1) return null;
  return (
    <BtnGhost
      type="button"
      style={{ fontSize: 11, width: 'auto', opacity: markingAllRead ? 0.55 : 1, ...style }}
      disabled={markingAllRead}
      onClick={onMarcarTodas}
    >
      {markingAllRead ? 'Marcando…' : 'Marcar todas como leídas'}
    </BtnGhost>
  );
}

/** Pestaña con badge de sin leer. */
export function AdminSeguimientoTabLabel({ label, unreadCount, title }) {
  return (
    <>
      <span>{label}</span>
      {unreadCount > 0 ? (
        <>
          <AdminAlertDot title={title || `${unreadCount} sin leer`} />
          <AdminAlertCount count={unreadCount} />
        </>
      ) : null}
    </>
  );
}

/** Celda checkbox «Leída». */
export function AdminLeidaCell({ leida, busy, onChange, title }) {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: busy ? 'wait' : 'pointer',
        fontSize: 10,
        color: leida ? 'rgba(255,255,255,0.45)' : C.gold,
        fontWeight: leida ? 500 : 700,
        opacity: busy ? 0.55 : 1,
      }}
      title={title || (leida ? 'Marcar como no leída' : 'Marcar como leída')}
    >
      <input
        type="checkbox"
        checked={leida}
        disabled={busy}
        onChange={e => onChange(e.target.checked)}
        style={{ accentColor: C.gold, cursor: busy ? 'wait' : 'pointer' }}
      />
      <span>{leida ? 'Sí' : 'No'}</span>
    </label>
  );
}

/** Estilo de fila si está sin leer. */
export function adminSeguimientoRowStyle(sinLeer, base = {}) {
  return {
    ...base,
    background: sinLeer ? 'rgba(226,75,74,0.06)' : base.background || 'transparent',
  };
}
